import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Download, X } from 'lucide-react';
import { leadApi, meApi } from '../services/api';
import { Lead, LeadFilters } from '../types';
import { STAGES, STAGE_LABEL, fmtAgo, fmtDayTime, isLate, label } from '../lib';
import { MyLeadsSummary } from '../types';
import { useCallWindows } from '../context/CallWindowsContext';
import { Empty, LeadCell, Loading, ReachButtons, StageChip } from '../components/WorkBits';
import { useCallModal, useChatSheet } from './queueBits';
import { buildCsv, downloadCsv, generateExportFilename } from '../utils/leadExport';

const SORT_OPTIONS = [
  { value: 'next_action_at:asc', label: 'Sort: Next action (soonest)' },
  { value: 'last_activity_at:desc', label: 'Sort: Last activity (recent)' },
  { value: 'created_at:desc', label: 'Sort: Newest first' },
  { value: 'created_at:asc', label: 'Sort: Oldest first' },
];

/** Saved views — the cohorts operators rebuild by hand every day, as one click.
 *  Each is a real `GET /leads` filter, not a client-side approximation.
 *  visit_status is THE pipeline dimension, so the views lead with it. */
const VIEWS: { label: string; filters: LeadFilters }[] = [
  { label: 'Visit committed', filters: { tag: 'visit_status:committed' } },
  { label: 'Interested, no date', filters: { tag: 'visit_status:interested_no_date' } },
  { label: 'Serial reschedulers', filters: { tag: 'visit_status:serial_rescheduler' } },
  { label: 'No-shows', filters: { tag: 'visit_status:committed_no_show' } },
  { label: 'Visited', filters: { stage: 'visited' } },
  { label: 'Untagged', filters: { has_tags: false } },
];

const sameView = (a: LeadFilters, b: LeadFilters) => JSON.stringify(a) === JSON.stringify(b);

// Persist list state across navigation
const STORAGE_KEY = 'leadlist_state';
const saveListState = (state: any) => sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
const loadListState = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const LeadList: React.FC = () => {
  const navigate = useNavigate();
  const { call, modal } = useCallModal();
  const { chatLead, sheet } = useChatSheet();
  const windows = useCallWindows();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep links from the tags dashboard (/leads?tag=… or ?has_tags=…) take
  // precedence over the sessionStorage restore.
  const urlTag = searchParams.get('tag') || undefined;
  const urlHasTags = searchParams.get('has_tags');
  const urlSeeded = urlTag !== undefined || urlHasTags !== null;
  const saved = urlSeeded ? null : loadListState();

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<MyLeadsSummary | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);
  const limit = 50;

  const [filters, setFilters] = useState<LeadFilters>(
    urlSeeded
      ? {
        ...(urlTag ? { tag: urlTag } : {}),
        ...(urlHasTags !== null ? { has_tags: urlHasTags === 'true' } : {}),
      }
      : saved?.filters || {},
  );
  const [searchInput, setSearchInput] = useState(saved?.searchInput || '');
  const [sortBy, setSortBy] = useState(saved?.sortBy || 'next_action_at');
  const [sortOrder, setSortOrder] = useState(saved?.sortOrder || 'asc');

  // Infinite scroll: the first request restores however many rows were on
  // screen when the operator left (whole pages), later ones append a page.
  const fetchLeads = async () => {
    setLoading(true);
    try {
      const restore = saved?.count ? Math.min(Math.ceil(saved.count / limit) * limit, 500) : limit;
      const response = await leadApi.list(1, restore, filters, sortBy, sortOrder);
      setLeads(response.data);
      setTotal(response.total);

      const scrollY = saved?.scrollY;
      if (scrollY) {
        requestAnimationFrame(() => document.getElementById('shell-main')?.scrollTo(0, scrollY));
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
    setLoading(false);
  };

  const fetchMore = async () => {
    if (loadingMore || leads.length >= total) return;
    setLoadingMore(true);
    try {
      const response = await leadApi.list(Math.floor(leads.length / limit) + 1, limit, filters, sortBy, sortOrder);
      setLeads((prev) => [...prev, ...response.data]);
    } catch (error) {
      console.error('Error fetching more leads:', error);
    }
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortBy, sortOrder]);

  useEffect(() => { meApi.leadsSummary().then(setSummary).catch((e) => console.error('leads summary failed', e)); }, []);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) fetchMore(); }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  });

  const navigateToLead = (leadId: string) => {
    saveListState({
      filters, searchInput, sortBy, sortOrder, count: leads.length,
      scrollY: document.getElementById('shell-main')?.scrollTop ?? 0,
    });
    navigate(`/leads/${leadId}`);
  };

  const applyFilters = (next: LeadFilters) => {
    setFilters(next);
    if (urlSeeded) setSearchParams({}, { replace: true });
  };

  // A search spans the whole book — a typed name, phone or Sell.do id should
  // find that lead whatever its stage (dropped leads included) — so it drops
  // the stage pill.
  const submitSearch = () => applyFilters({
    ...filters, stage: searchInput ? undefined : filters.stage, search: searchInput || undefined,
  });

  const handleFilterChange = (key: keyof LeadFilters, value: string | boolean | undefined) =>
    applyFilters({ ...filters, [key]: value === '' ? undefined : value });

  // Every lead matching the filter, not just the rows on screen — walked in
  // core's largest page.
  const handleExport = async () => {
    setExporting(true);
    try {
      const all: Lead[] = [];
      for (let page = 1; all.length < total; page++) {
        const r = await leadApi.list(page, 500, filters, sortBy, sortOrder);
        all.push(...r.data);
        if (r.data.length < 500) break;
      }
      downloadCsv(generateExportFilename(), buildCsv(all));
    } finally {
      setExporting(false);
    }
  };

  const activeFilterCount = Object.keys(filters).length;

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head">
        <div>
          <h1 className="page-title">Leads</h1>
          <p className="page-sub num">{total.toLocaleString()} matching this filter</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            className="inp"
            value={`${sortBy}:${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split(':');
              setSortBy(field);
              setSortOrder(order);
            }}
          >
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button className="btn flex items-center gap-2" onClick={() => setShowFilters(!showFilters)}>
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.75} />
            Filters
            {activeFilterCount > 0 && <span className="chip chip-forest num">{activeFilterCount}</span>}
          </button>
          <button
            className="btn flex items-center gap-2"
            onClick={handleExport}
            disabled={total === 0 || exporting}
            title="Export every lead matching this filter as Sell.Do CSV"
          >
            <Download className="w-4 h-4" strokeWidth={1.75} />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3">
        <button className="pill whitespace-nowrap" aria-pressed={!filters.stage} onClick={() => handleFilterChange('stage', undefined)}>
          All {summary && <span className="num opacity-70 ml-0.5">{summary.total}</span>}
        </button>
        {STAGES.filter((st) => summary?.by_stage[st]).map((st) => (
          <button key={st} className="pill whitespace-nowrap" aria-pressed={filters.stage === st}
            onClick={() => handleFilterChange('stage', filters.stage === st ? undefined : st)}>
            {STAGE_LABEL[st]} <span className="num opacity-70 ml-0.5">{summary!.by_stage[st]}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3.5">
        <span className="font-display text-[10.5px] font-semibold tracking-[0.11em] uppercase text-graphite">
          Views
        </span>
        {VIEWS.map((v) => (
          <button
            key={v.label}
            className="pill"
            aria-pressed={sameView(filters, v.filters)}
            onClick={() => applyFilters(sameView(filters, v.filters) ? {} : v.filters)}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-3.5">
        <input
          className="inp flex-1 min-w-[220px]"
          placeholder="Search by name, phone or Sell.do id…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitSearch()}
        />
        <button
          className="btn btn-pri"
          onClick={() => submitSearch()}
        >
          Search
        </button>
        {(filters.tag || filters.has_tags !== undefined) && (
          <button
            className="pill flex items-center gap-1.5"
            aria-pressed
            title="Remove this filter"
            onClick={() => {
              const { tag, has_tags, ...rest } = filters;
              applyFilters(rest);
            }}
          >
            {filters.tag ? label(filters.tag) : filters.has_tags ? 'has tags' : 'untagged'}
            <X className="w-3 h-3" strokeWidth={2} />
          </button>
        )}
      </div>

      {showFilters && (
        <div className="card mb-3.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-graphite mb-1">Source</span>
              <input
                className="inp w-full"
                value={filters.source || ''}
                onChange={(e) => handleFilterChange('source', e.target.value)}
                placeholder="e.g. meta_ads"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-graphite mb-1">Location</span>
              <input
                className="inp w-full"
                value={filters.location || ''}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                placeholder="e.g. Hyderabad"
              />
            </label>
          </div>
          {activeFilterCount > 0 && (
            <div className="mt-3 flex justify-end">
              <button
                className="text-[13px] text-graphite hover:text-charcoal"
                onClick={() => { setSearchInput(''); applyFilters({}); }}
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? <Loading what="leads" /> : leads.length === 0 ? (
        <Empty>No lead matches this filter.</Empty>
      ) : (
        <>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Lead</th><th>Stage</th><th>Last activity</th><th>Next action</th>
                  <th className="text-right">Reach</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} onClick={() => navigateToLead(lead.id)}>
                    <td><LeadCell name={lead.name} sub={lead.mobile || lead.email} /></td>
                    <td><StageChip stage={lead.stage} /></td>
                    <td>
                      {lead.last_activity_at ? (
                        <>
                          <span className="capitalize">{label(lead.last_activity_type || 'activity')}</span>
                          <span className="block text-graphite text-[11.5px]">{fmtAgo(lead.last_activity_at)}</span>
                        </>
                      ) : <span className="text-graphite">No activity</span>}
                    </td>
                    <td>
                      {lead.next_action_at ? (
                        <>
                          <span className="capitalize">{label(lead.next_action_type || 'action')}</span>
                          <span className={`block text-[11.5px] num ${isLate(lead.next_action_at, windows) ? 'text-brick font-semibold' : 'text-graphite'}`}>
                            {fmtDayTime(lead.next_action_at)}
                          </span>
                        </>
                      ) : <span className="text-graphite">—</span>}
                    </td>
                    <td>
                      <ReachButtons lead={lead} onCall={() => call(lead)} onWhatsApp={() => chatLead(lead)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div ref={sentinel} className="text-center text-graphite text-[12.5px] py-4 num">
            {leads.length < total ? (loadingMore ? 'Loading more…' : '') : `All ${total.toLocaleString()} shown`}
          </div>
        </>
      )}

      {modal}
      {sheet}
    </div>
  );
};

export default LeadList;
